import { useEffect } from 'react'
import { createRootRoute, Outlet } from '@tanstack/react-router'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from '@/components/ui/toast'
import { SettingsModal } from '@/components/settings/SettingsModal'
import { SentryErrorBoundary } from '@/lib/sentry'
import { PostHogProvider } from 'posthog-js/react'

const CHUNK_RELOAD_KEY = 'chunk-reload-attempted'

function isStaleChunkError(error: Error): boolean {
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.name === 'ChunkLoadError'
  )
}

function RootErrorComponent({ error }: { error: Error }) {
  // A lazy import that fails after a deploy (the chunk's hash no longer
  // exists on the server) is caught by this boundary before it ever reaches
  // the window-level handler in main.tsx — so the reload-once lives here too.
  const staleChunk = isStaleChunkError(error)
  const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true'
  useEffect(() => {
    if (staleChunk && !alreadyReloaded) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true')
      window.location.reload()
    }
  }, [staleChunk, alreadyReloaded])

  if (staleChunk && !alreadyReloaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Updating to the latest version…</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Something went wrong</h1>
        <p className="text-muted-foreground">{error.message || 'An unexpected error occurred.'}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  )
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RootErrorComponent,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-muted-foreground">Page not found</p>
      </div>
    </div>
  ),
})

function RootLayout() {
  return (
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN}
      options={{
        api_host: '/ingest',
        ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        defaults: '2026-01-30',
        capture_exceptions: true,
        debug: import.meta.env.DEV,
      }}
    >
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem disableTransitionOnChange>
        <SentryErrorBoundary fallback={<div>Something went wrong</div>}>
          <DndProvider backend={HTML5Backend}>
            <Outlet />
          </DndProvider>
        </SentryErrorBoundary>
        <SettingsModal />
        <Toaster />
      </ThemeProvider>
    </PostHogProvider>
  )
}
